import { z } from 'zod';
import {
  canonicalStringify,
  hashCoasterProject,
  hashStoredCoasterProject,
  loadCoasterProject,
  type CoasterProject,
} from '../schema';

export type DraftRepositoryErrorCode =
  | 'unavailable'
  | 'not_found'
  | 'corrupt'
  | 'read_failed'
  | 'write_failed'
  | 'delete_failed'
  | 'quota_exceeded';

export type DraftRepositoryResult<T> =
  | { ok: true; value: T }
  | {
      ok: false;
      error: {
        code: DraftRepositoryErrorCode;
        message: string;
        recoverable: true;
        cause?: unknown;
      };
    };

export interface StoredDraftSummary {
  projectId: string;
  revisionId: string;
  title: string;
  savedAt: string;
  canonicalHash: string;
  corrupt?: boolean;
  errorMessage?: string;
}

export interface StoredDraft extends StoredDraftSummary {
  project: CoasterProject;
}

export interface WorkingDraftRepository {
  save(project: CoasterProject): Promise<DraftRepositoryResult<StoredDraft>>;
  load(projectId: string): Promise<DraftRepositoryResult<StoredDraft>>;
  list(): Promise<DraftRepositoryResult<StoredDraftSummary[]>>;
  delete(projectId: string): Promise<DraftRepositoryResult<void>>;
}

export interface StorageLike {
  readonly length: number;
  clear(): void;
  getItem(key: string): string | null;
  key(index: number): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

const StoredDraftEnvelopeSchema = z.object({
  envelopeVersion: z.literal(1),
  savedAt: z.iso.datetime({ offset: true }),
  canonicalHash: z.string().regex(/^[0-9a-f]{64}$/),
  project: z.unknown(),
});

export const LEGACY_V1_DRAFT_NAMESPACE = 'buncha-coasters:working-draft:v1:';
export const DEFAULT_DRAFT_NAMESPACE = 'buncha-coasters:working-draft:v2:';

function error<T>(
  code: DraftRepositoryErrorCode,
  message: string,
  cause?: unknown,
): DraftRepositoryResult<T> {
  return {
    ok: false,
    error: { code, message, recoverable: true, ...(cause === undefined ? {} : { cause }) },
  };
}

function isQuotaError(cause: unknown): boolean {
  return (
    cause instanceof DOMException &&
    (cause.name === 'QuotaExceededError' || cause.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

export class LocalWorkingDraftRepository implements WorkingDraftRepository {
  constructor(
    private readonly storage: StorageLike | null = typeof localStorage === 'undefined'
      ? null
      : localStorage,
    private readonly namespace = DEFAULT_DRAFT_NAMESPACE,
    private readonly now: () => Date = () => new Date(),
  ) {}

  private storageKey(projectId: string): string {
    return `${this.namespace}${encodeURIComponent(projectId)}`;
  }

  async save(projectValue: CoasterProject): Promise<DraftRepositoryResult<StoredDraft>> {
    if (!this.storage)
      return error('unavailable', 'Local draft storage is unavailable in this browser.');
    let project: CoasterProject;
    try {
      const loaded = loadCoasterProject(projectValue);
      if (!loaded.ok) throw new Error(loaded.error.message);
      project = loaded.project;
    } catch (cause) {
      return error('write_failed', 'The coaster is invalid and was not saved.', cause);
    }
    try {
      const canonicalHash = await hashCoasterProject(project);
      const savedAt = this.now().toISOString();
      this.storage.setItem(
        this.storageKey(project.projectId),
        canonicalStringify({ envelopeVersion: 1, savedAt, canonicalHash, project }),
      );
      return {
        ok: true,
        value: {
          projectId: project.projectId,
          revisionId: project.revisionId,
          title: project.metadata.title,
          savedAt,
          canonicalHash,
          project,
        },
      };
    } catch (cause) {
      return error(
        isQuotaError(cause) ? 'quota_exceeded' : 'write_failed',
        isQuotaError(cause)
          ? 'Local draft storage is full.'
          : 'The local draft could not be saved.',
        cause,
      );
    }
  }

  async load(projectId: string): Promise<DraftRepositoryResult<StoredDraft>> {
    if (!this.storage)
      return error('unavailable', 'Local draft storage is unavailable in this browser.');
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.storageKey(projectId));
    } catch (cause) {
      return error('read_failed', 'The local draft could not be read.', cause);
    }
    if (raw === null) return error('not_found', 'No local draft exists for this coaster.');
    try {
      const envelopeResult = StoredDraftEnvelopeSchema.safeParse(JSON.parse(raw) as unknown);
      if (!envelopeResult.success)
        return error(
          'corrupt',
          'The saved draft envelope is damaged.',
          envelopeResult.error.issues,
        );
      const sourceHash = await hashStoredCoasterProject(envelopeResult.data.project);
      if (sourceHash !== envelopeResult.data.canonicalHash)
        return error('corrupt', 'The saved draft failed its integrity check.');
      const loaded = loadCoasterProject(envelopeResult.data.project);
      if (!loaded.ok) return error('corrupt', loaded.error.message, loaded.error);
      const canonicalHash = await hashCoasterProject(loaded.project);
      return {
        ok: true,
        value: {
          projectId: loaded.project.projectId,
          revisionId: loaded.project.revisionId,
          title: loaded.project.metadata.title,
          savedAt: envelopeResult.data.savedAt,
          canonicalHash,
          project: loaded.project,
        },
      };
    } catch (cause) {
      return error('corrupt', 'The saved draft is unreadable.', cause);
    }
  }

  async list(): Promise<DraftRepositoryResult<StoredDraftSummary[]>> {
    if (!this.storage)
      return error('unavailable', 'Local draft storage is unavailable in this browser.');
    try {
      const projectIds: string[] = [];
      for (let index = 0; index < this.storage.length; index += 1) {
        const key = this.storage.key(index);
        if (key?.startsWith(this.namespace))
          projectIds.push(decodeURIComponent(key.slice(this.namespace.length)));
      }
      const summaries: StoredDraftSummary[] = [];
      for (const projectId of projectIds) {
        const loaded = await this.load(projectId);
        if (!loaded.ok) return loaded;
        const { project, ...summary } = loaded.value;
        void project;
        summaries.push(summary);
      }
      summaries.sort(
        (a, b) => b.savedAt.localeCompare(a.savedAt) || a.projectId.localeCompare(b.projectId),
      );
      return { ok: true, value: summaries };
    } catch (cause) {
      return error('read_failed', 'Local drafts could not be listed.', cause);
    }
  }

  async delete(projectId: string): Promise<DraftRepositoryResult<void>> {
    if (!this.storage)
      return error('unavailable', 'Local draft storage is unavailable in this browser.');
    try {
      this.storage.removeItem(this.storageKey(projectId));
      return { ok: true, value: undefined };
    } catch (cause) {
      return error('delete_failed', 'The local draft could not be deleted.', cause);
    }
  }
}

export function createLocalWorkingDraftRepository(): WorkingDraftRepository {
  return new LocalWorkingDraftRepository();
}
